'use client';

import { useEffect, useState } from 'react';
import { getJobs } from '@/lib/actions/jobs';
import { Link } from '@/i18n/routing';
import {
  Plus,
  Pencil,
  Briefcase,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DeleteJobButton } from '@/components/admin/careers/DeleteJobButton';

export default function AdminCareersPage() {
  const [jobs, setJobs] = useState<Awaited<ReturnType<typeof getJobs>>>([]);

  useEffect(() => {
    getJobs().then(setJobs);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy">Manage Careers</h1>
          <p className="text-muted-foreground mt-1">Add and manage open positions.</p>
        </div>
        <Button asChild className="bg-primary hover:bg-navy text-primary-foreground font-semibold">
          <Link href="/admin/careers/new">
            <Plus className="mr-2 h-4 w-4" /> Add Position
          </Link>
        </Button>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job Title</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <Briefcase size={40} className="text-gray-300" />
                    <p>No job positions found. Hire some talent!</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-semibold">{job.title}</span>
                      <span className="text-xs text-gray-400">{job.slug}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{job.location || 'N/A'}</TableCell>
                  <TableCell>
                    {job.isActive ? (
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">Active</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-gray-400">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" asChild title="Edit">
                        <Link href={`/admin/careers/${job.id}`}>
                          <Pencil className="h-4 w-4 text-blue-600" />
                        </Link>
                      </Button>
                      <DeleteJobButton id={job.id} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
